import * as XLSX from "xlsx";

/**
 * Encabezado informativo de los reportes exportados a Excel.
 *
 * El PDF de los reportes ya dice de que es la informacion (bloque de empresa mas el
 * titulo/subtitulo de `PrintDocumentHeader`), pero la hoja de Excel se armaba solo con las
 * columnas y los datos. Estas utilidades construyen las filas AOA que `ReportPage`
 * antepone a la tabla para que el archivo sea autoexplicativo.
 */

export type ExcelHeadingDetail = {
  label: string,
  value: string
}

export type ExcelHeading = {
  title: string,
  details?: ExcelHeadingDetail[]
}

/**
 * Filas AOA del encabezado, incluida la fila en blanco que lo separa de las columnas.
 *
 * Sin `heading` devuelve `[]`, para que los reportes que no lo configuran mantengan el
 * layout historico (columnas en la fila 1). Los detalles sin valor se omiten para no
 * dejar filas huerfanas cuando un nombre no se pudo cargar.
 */
export const buildExcelHeadingRows = (heading?: ExcelHeading): string[][] => {
  if (!heading) {
    return [];
  }

  const detailRows = (heading.details || [])
    .filter((detail) => `${detail.value ?? ""}`.trim() !== "")
    .map((detail) => [detail.label, `${detail.value}`]);

  return [[heading.title], ...detailRows, []];
}

/**
 * Columna y fila minimas que necesita el libro de Excel. Son subconjuntos
 * estructurales de `ReportTitleProps` / `ReportDataProps` de `ReportPage`: se
 * declaran aqui para que este modulo no dependa del componente (que si depende
 * de este) y para poder armar libros sin renderizar nada.
 */
export type ReportWorkbookColumn = {
  label: string,
  isNumeric?: boolean
}

export type ReportWorkbookRow = {
  values: string[]
}

export const REPORT_EXCEL_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Arma el libro de Excel de un reporte y devuelve sus bytes.
 *
 * Vive fuera de `ReportPage` porque el mismo libro se necesita en dos destinos:
 * la descarga directa del navegador (`saveAs`) y el paquete contable, que lo
 * sube a S3 para que la lambda lo comprima en el ZIP.
 *
 * Con `rows` vacio devuelve el libro con solo el encabezado y las columnas: un
 * periodo sin movimientos debe producir archivo igual, no quedarse sin generar.
 */
export const buildReportWorkbookBytes = (
  columns: ReportWorkbookColumn[],
  rows: ReportWorkbookRow[],
  heading?: ExcelHeading
): Uint8Array => {
  const headers = columns.map((column) => column.label);

  const detailRows = rows.map((row) => {
    return row.values.map((value, index) => {
      const column = columns[index];
      if (column?.isNumeric) {
        const numericValue = Number(String(value).replace(",", "."));
        return isNaN(numericValue) ? value : numericValue;
      }
      return value;
    });
  });

  const headingRows = buildExcelHeadingRows(heading);
  const sheetDetail = XLSX.utils.aoa_to_sheet([...headingRows, headers, ...detailRows]);

  if (headingRows.length > 0) {
    /** El titulo ocupa el ancho de la tabla para que no lo tape la segunda columna. */
    sheetDetail["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(headers.length - 1, 1) } }
    ];
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheetDetail, "Detalle");

  return new Uint8Array(XLSX.write(workbook, { type: "array", bookType: "xlsx" }));
}
